import * as THREE from 'three/webgpu';
import type {
    SectionViewCapRenderStageSource,
} from '../../types/section-view/SectionViewTypes';

/**
 * 支持多剖面盖的主场景 PassNode。
 *
 * 生命周期与边界：
 * 1. 复用 PassNode 唯一的颜色/深度模板 RenderTarget，不创建额外离屏资源；
 * 2. 每个盖面 Scene 都通过独立 renderer.render() 建立真实 render-pass 边界；
 * 3. 第一盖面清颜色、深度与 stencil，后续盖面只清 stencil，主场景最后加载已有附件；
 * 4. 主模型每次管线提交只绘制一次，异常时恢复调用方全部 renderer 状态；
 * 5. RenderTarget 的创建、尺寸同步和释放继续由 PassNode 统一负责。
 */
export class SectionViewScenePassNode extends THREE.PassNode {
    /** 主模型场景。 */
    private readonly mainScene: THREE.Scene;
    /** 提供稳定剖面盖子阶段 Scene 的渲染控制端口。 */
    private readonly capRenderStageSource: SectionViewCapRenderStageSource;

    /**
     * 创建支持剖面盖子阶段的主场景 Pass。
     *
     * @param mainScene 主模型场景。
     * @param camera 当前活动相机。
     * @param capRenderStageSource 稳定盖面子阶段来源。
     */
    public constructor(
        mainScene: THREE.Scene,
        camera: THREE.Camera,
        capRenderStageSource: SectionViewCapRenderStageSource,
    ) {
        super(THREE.PassNode.COLOR, mainScene, camera, { stencilBuffer: true });
        this.mainScene = mainScene;
        this.capRenderStageSource = capRenderStageSource;
    }

    /**
     * 更新主场景纹理，并为每个活动剖面建立独立 stencil 生命周期。
     *
     * @param frame Three.js 节点帧上下文。
     * @returns 无返回值。
     * @throws 渲染器缺失、子阶段来源非法或底层绘制失败时抛出错误。
     */
    public override updateBefore(frame: THREE.NodeFrame): undefined {
        if (!frame.renderer) {
            throw new Error('剖面主场景 Pass 缺少渲染器，无法提交渲染');
        }

        const renderer = frame.renderer as THREE.WebGPURenderer;
        const activeStageCount = this.capRenderStageSource.getActiveCapRenderStageCount();
        if (!Number.isInteger(activeStageCount) || activeStageCount < 0) {
            throw new Error(`剖面盖子阶段数量非法: count=${activeStageCount}`);
        }

        const currentRenderTarget = renderer.getRenderTarget();
        const currentMRT = renderer.getMRT();
        const currentAutoClear = renderer.autoClear;
        const currentAutoClearColor = renderer.autoClearColor;
        const currentAutoClearDepth = renderer.autoClearDepth;
        const currentAutoClearStencil = renderer.autoClearStencil;
        const currentTransparent = renderer.transparent;
        const currentOpaque = renderer.opaque;
        const currentContextNode = renderer.contextNode;
        const currentPassScene = this.scene;
        const currentMainSceneName = this.mainScene.name;
        const currentMainOverrideMaterial = this.mainScene.overrideMaterial;
        const shouldUpdateMainSceneMatrixWorld = this.mainScene.matrixWorldAutoUpdate;

        const outputRenderTarget = renderer.getOutputRenderTarget();
        const renderCamera = outputRenderTarget?.isXRRenderTarget === true
            ? renderer.xr.getCamera()
            : this.camera;
        const currentCameraLayerMask = renderCamera.layers.mask;
        let firstStageScene: THREE.Scene | null = null;
        let currentFirstStageName = '';
        let currentFirstStageOverrideMaterial: THREE.Material | null = null;

        try {
            if (activeStageCount === 0) {
                // 无盖面时完全沿用 Three.js PassNode 的单场景提交语义。
                super.updateBefore(frame);
                return;
            }

            firstStageScene = this.capRenderStageSource.getCapRenderStageScene(0);
            if (!firstStageScene || firstStageScene.isScene !== true) {
                throw new Error('剖面盖子阶段来源必须返回 THREE.Scene');
            }
            currentFirstStageName = firstStageScene.name;
            currentFirstStageOverrideMaterial = firstStageScene.overrideMaterial;

            if (shouldUpdateMainSceneMatrixWorld) {
                /**
                 * 盖面先于主场景绘制，Three 不会替不属于 stage Scene 的源网格更新矩阵。
                 * 因此按 Renderer 的 matrixWorldAutoUpdate 语义预先刷新一次，代理即可在同帧读取最新矩阵。
                 */
                this.mainScene.updateMatrixWorld();
            }

            renderer.autoClearColor = true;
            renderer.autoClearDepth = true;
            renderer.autoClearStencil = true;
            this.scene = firstStageScene;
            // 复用 PassNode 完成尺寸、XR、纹理轮换、相机参数和首个 Clear/Clear/Clear 子阶段。
            super.updateBefore(frame);

            this.scene = this.mainScene;
            renderer.setRenderTarget(this.renderTarget);
            renderer.setMRT(this.getMRT());
            renderer.transparent = this.transparent;
            renderer.opaque = this.opaque;

            const passLayers = this.getLayers();
            if (passLayers) {
                renderCamera.layers.mask = passLayers.mask;
            }

            for (let stageIndex = 1; stageIndex < activeStageCount; stageIndex += 1) {
                const stageScene = this.capRenderStageSource.getCapRenderStageScene(stageIndex);
                if (!stageScene || stageScene.isScene !== true) {
                    throw new Error(`剖面盖子阶段来源必须返回 THREE.Scene: index=${stageIndex}`);
                }

                /**
                 * 颜色与深度保留前序结果，只清当前共享附件中的 stencil。
                 * WebGPU 后端据四个 clear 标志创建 Color=Load、Depth=Load、Stencil=Clear 的新 render pass。
                 */
                renderer.autoClear = true;
                renderer.autoClearColor = false;
                renderer.autoClearDepth = false;
                renderer.autoClearStencil = true;
                renderer.render(stageScene, renderCamera);
            }

            renderer.autoClear = false;
            if (this.overrideMaterial !== null) {
                this.mainScene.overrideMaterial = this.overrideMaterial;
            }
            if (this.name) {
                this.mainScene.name = this.name;
            }
            if (shouldUpdateMainSceneMatrixWorld) {
                // 源矩阵已在盖面前更新，主场景提交时禁止重复遍历整棵模型树。
                this.mainScene.matrixWorldAutoUpdate = false;
            }
            renderer.render(this.mainScene, renderCamera);
        } finally {
            this.mainScene.matrixWorldAutoUpdate = shouldUpdateMainSceneMatrixWorld;
            this.mainScene.name = currentMainSceneName;
            this.mainScene.overrideMaterial = currentMainOverrideMaterial;
            if (firstStageScene) {
                firstStageScene.name = currentFirstStageName;
                firstStageScene.overrideMaterial = currentFirstStageOverrideMaterial;
            }
            this.scene = currentPassScene;
            renderer.setRenderTarget(currentRenderTarget);
            renderer.setMRT(currentMRT);
            renderer.autoClear = currentAutoClear;
            renderer.autoClearColor = currentAutoClearColor;
            renderer.autoClearDepth = currentAutoClearDepth;
            renderer.autoClearStencil = currentAutoClearStencil;
            renderer.transparent = currentTransparent;
            renderer.opaque = currentOpaque;
            renderer.contextNode = currentContextNode;
            renderCamera.layers.mask = currentCameraLayerMask;
        }
    }
}
